import { generateHTML } from "@tiptap/html";
import moment from "moment";
import { useEffect, useState } from "react";
import ExtensionKit from "../tiptap/extenstions/extension-kit";
import { useConfigStore } from "../../Stores/Config";
import { useSessionStore } from "../../Stores/Session";
import { useChangelogCommentsStore } from "../../Stores/ChangelogComments";
import {
  canWriteChangelogComment,
  changelogCommentsEnabled,
  commentAuthorName,
} from "../../Helper/ChangelogComments";
import { sanitizeUserContent } from "../../Helper/sanitize";
import type { ChangelogComment } from "../../Services/http.clients/http.changelog.client";
import "./ChangelogComments.scss";

const avatarColors = [
  "#6366f1",
  "#8b5cf6",
  "#a855f7",
  "#ec4899",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#14b8a6",
  "#06b6d4",
  "#3b82f6",
  "#6d28d9",
];

const avatarColorOf = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

// Portal comments are TipTap documents; older or API-made ones plain text.
// Other visitors wrote them, so the HTML is sanitised before it is shown.
const commentHtml = (comment: ChangelogComment) => {
  const content = comment.data?.content;
  try {
    if (content && typeof content === "object" && content.type) {
      return sanitizeUserContent(generateHTML(content, ExtensionKit()));
    }
  } catch (_) {}
  const div = document.createElement("div");
  div.textContent = typeof content === "string" ? content : "";
  return div.innerHTML;
};

interface CommentItemProps {
  comment: ChangelogComment;
  guestLabel: string;
  isReply?: boolean;
}

const CommentItem = ({ comment, guestLabel, isReply }: CommentItemProps) => {
  const name = commentAuthorName(comment, guestLabel);
  const image = comment.user?.profileImageUrl;
  const isTeamMember = !!comment.user;

  return (
    <div
      className={`changelog-comments-item ${isReply ? "changelog-comments-item--reply" : ""}`}
    >
      {image ? (
        <img className="changelog-comments-avatar" src={image} alt="" />
      ) : (
        <div
          className="changelog-comments-avatar changelog-comments-avatar--initial"
          style={{ backgroundColor: avatarColorOf(name) }}
          aria-hidden="true"
        >
          {name.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="changelog-comments-bubble">
        <div className="changelog-comments-meta">
          <span className="changelog-comments-name">{name}</span>
          {isTeamMember && (
            <svg
              className="changelog-comments-badge"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          )}
          {comment.createdAt && (
            <span className="changelog-comments-date">
              {moment(comment.createdAt).fromNow()}
            </span>
          )}
        </div>
        <div
          className="changelog-comments-text"
          dangerouslySetInnerHTML={{ __html: commentHtml(comment) }}
        />
      </div>
    </div>
  );
};

interface ChangelogCommentsProps {
  changelogId: string;
}

/**
 * The discussion under a changelog in the widget (#149751): the same comments
 * as on the feedback portal, so a changelog meant for signed-in users only can
 * be discussed where those users see it.
 */
export const ChangelogComments = ({ changelogId }: ChangelogCommentsProps) => {
  const configStore = useConfigStore();
  const config = configStore.config;
  const session = useSessionStore((state) => state.session);
  const { comments, available, loading, sending, loadComments, addComment } =
    useChangelogCommentsStore();
  const [text, setText] = useState("");
  const [hasError, setHasError] = useState(false);
  const isEnabled = changelogCommentsEnabled(config);
  const canWrite = canWriteChangelogComment(config, session);
  const guestLabel = configStore.translateTextWithFallback("guest", "Guest");
  const commentCount = comments.reduce(
    (count, comment) => count + 1 + (comment.replies?.length ?? 0),
    0,
  );

  useEffect(() => {
    if (isEnabled && changelogId) {
      loadComments(changelogId);
    }
  }, [changelogId, isEnabled, session?.gleapId]);

  if (!isEnabled || !available) {
    return null;
  }

  // Nothing to read and nothing to write: no empty section.
  if (!canWrite && comments.length === 0) {
    return null;
  }

  const handleSend = async () => {
    setHasError(false);
    const isSent = await addComment(changelogId, text);
    if (isSent) {
      setText("");
      return;
    }
    setHasError(true);
  };

  return (
    <div className="changelog-comments">
      <div className="changelog-comments-title" role="heading" aria-level={2}>
        {configStore.translateTextWithFallback("comments", "Comments")}
        {commentCount > 0 && (
          <span className="changelog-comments-count">{commentCount}</span>
        )}
      </div>
      {comments.length === 0 && !loading && (
        <div className="changelog-comments-empty">
          {configStore.translateText("noComments")}
        </div>
      )}
      {comments.length > 0 && (
        <div className="changelog-comments-list">
          {comments.map((comment, index) => (
            <div key={comment.id ?? comment._id ?? index}>
              <CommentItem comment={comment} guestLabel={guestLabel} />
              {(comment.replies ?? []).map((reply, replyIndex) => (
                <CommentItem
                  key={reply.id ?? reply._id ?? replyIndex}
                  comment={reply}
                  guestLabel={guestLabel}
                  isReply
                />
              ))}
            </div>
          ))}
        </div>
      )}
      {canWrite && (
        <div className="changelog-comments-composer">
          <textarea
            className="changelog-comments-input"
            aria-label={configStore.translateText("writeComment")}
            placeholder={configStore.translateText("writeComment")}
            value={text}
            maxLength={5000}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="changelog-comments-actions">
            {hasError && (
              <span className="changelog-comments-error" role="alert">
                {configStore.translateTextWithFallback(
                  "somethingWentWrong",
                  "Something went wrong. Please try again.",
                )}
              </span>
            )}
            <button
              className="changelog-comments-send"
              onClick={handleSend}
              disabled={!text.trim() || sending}
            >
              {configStore.translateText("send")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
